import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from './cn';
import { IconButton } from './Button';

export interface DialogProps {
  open: boolean;
  onOpenChange(open: boolean): void;
  title: ReactNode;
  /** One line under the title saying what happens. */
  description?: ReactNode;
  /** Action buttons, right-aligned; the primary action goes last. */
  footer?: ReactNode;
  /** Compact navigation/actions beside the title, within the focus-trapped surface. */
  headerActions?: ReactNode;
  /** sm 400px, md 520px (default), lg 720px, xl 1100px. */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Classes for the scrolling body (e.g. less padding for a menu). */
  bodyClassName?: string;
  /** Restore focus when opened without a Radix trigger, including nested photo viewers. */
  onCloseAutoFocus?(event: Event): void;
  children?: ReactNode;
}

const overlay = 'fixed inset-0 z-50 bg-navy-ink/40';

/** Shared header / body / footer of Dialog and Sheet. */
function Frame({ title, description, footer, headerActions, children, bodyClassName, onClose }: Omit<DialogProps, 'open' | 'onOpenChange' | 'size' | 'onCloseAutoFocus'> & { onClose(): void }) {
  const { t } = useTranslation();
  return (
    <>
      <div className="flex shrink-0 items-start gap-3 border-b border-line px-4 py-3.5 sm:px-5 sm:py-4">
        <div className="min-w-0 flex-1">
          <RadixDialog.Title className="m-0 font-display text-lg font-semibold text-heading">{title}</RadixDialog.Title>
          {description ? (
            <RadixDialog.Description className="m-0 mt-1 text-sm text-ink-muted">{description}</RadixDialog.Description>
          ) : (
            <RadixDialog.Description className="sr-only">{title}</RadixDialog.Description>
          )}
        </div>
        {headerActions && <div className="flex shrink-0 items-center gap-2">{headerActions}</div>}
        <IconButton label={t('actions.close')} icon={<X />} size="sm" onClick={onClose} className="-mr-1.5 -mt-0.5" />
      </div>
      {children !== undefined && <div className={cn('min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-5', bodyClassName)}>{children}</div>}
      {/* the footer stays put below the scrolling body; on phones it clears the home indicator */}
      {footer && (
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-line bg-subtle px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-5">{footer}</div>
      )}
    </>
  );
}

/**
 * Modal dialog for a short task or decision. Closes on Esc and outside click; focus is trapped
 * inside and returns to the opener. For longer forms use Sheet.
 */
export function Dialog({ open, onOpenChange, onCloseAutoFocus, size = 'md', ...frame }: DialogProps) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className={overlay} />
        <RadixDialog.Content
          onCloseAutoFocus={onCloseAutoFocus}
          className={cn(
            // phones: nearly the whole screen (dvh follows the browser bars), body scrolls, footer stays
            'fixed left-1/2 top-[max(1rem,6dvh)] z-50 flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-1.5rem)] -translate-x-1/2 flex-col overflow-hidden rounded-xl border border-line bg-surface font-sans text-base text-ink shadow-pop outline-none',
            'sm:top-[12vh] sm:max-h-[76vh] sm:w-[calc(100vw-2rem)]',
            size === 'sm' ? 'max-w-[400px]' : size === 'xl' ? 'max-w-[1100px]' : size === 'lg' ? 'max-w-[720px]' : 'max-w-[520px]',
          )}
        >
          <Frame {...frame} onClose={() => onOpenChange(false)} />
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

export interface SheetProps extends Omit<DialogProps, 'size'> {
  /** sm 420px, md 560px (default), lg 760px, xl 1200px; always the full width of a phone. */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * right (default) for forms and details; left for navigation; bottom for a short panel of
   * choices on phones (e.g. filters), up to 85% of the screen high.
   */
  side?: 'right' | 'left' | 'bottom';
  /**
   * false docks the sheet beside the page instead: no overlay, the page stays usable (e.g. picking
   * a spot on a map while editing) and outside clicks do not close it; Esc and Close still do.
   */
  modal?: boolean;
}

/**
 * Side panel sliding in from the right: forms and details that need room while keeping the page
 * in view (create/edit forms, refunds, record previews).
 */
export function Sheet({ open, onOpenChange, onCloseAutoFocus, size = 'md', side = 'right', modal = true, ...frame }: SheetProps) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange} modal={modal}>
      <RadixDialog.Portal>
        {modal && <RadixDialog.Overlay className={overlay} />}
        <RadixDialog.Content
          onCloseAutoFocus={onCloseAutoFocus}
          onInteractOutside={modal ? undefined : (e) => e.preventDefault()}
          className={cn(
            'fixed z-50 flex flex-col border-line bg-surface font-sans text-base text-ink shadow-pop outline-none',
            side === 'bottom'
              ? 'inset-x-0 bottom-0 max-h-[85dvh] rounded-t-xl border-t'
              : cn(
                  'inset-y-0 w-full',
                  side === 'left' ? 'left-0 max-w-[min(20rem,85vw)] border-r' : 'right-0 border-l',
                  side === 'right' && (size === 'xl' ? 'sm:max-w-[1200px]' : size === 'sm' ? 'sm:max-w-[420px]' : size === 'lg' ? 'sm:max-w-[760px]' : 'sm:max-w-[560px]'),
                ),
          )}
        >
          <Frame {...frame} onClose={() => onOpenChange(false)} />
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
